import { Box, Container, Spinner, Text } from '@bpba/ui-components';
import { EstudiosEconomicosResponse } from '../rest/Queries';
import { useQuery } from '@apollo/client';
import { getEstudiosEconomicos } from '../rest/queries/getEstudiosEconomicos';
import styled, { keyframes } from 'styled-components';
import { InformesAnteriores } from '../components/InformesAnteriores';

export const EstudiosEconomicos = () => {
  const { data: { getEstudios } = {}, loading, error } = useQuery<EstudiosEconomicosResponse>(getEstudiosEconomicos);
  const estudiosFailed = !loading && (!!error || !getEstudios);

  return (
    <Container mb={8} style={{ minHeight: 'calc(100dvh - 120px)' }}>
      {loading && (
        <Box justifyContent="center" alignItems="center" style={{ minHeight: '320px' }}>
          <Spinner size="48px" color="primary.0" aria-label="Cargando estudios económicos" />
        </Box>
      )}
      {estudiosFailed && (
        <Box justifyContent="center" alignItems="center" style={{ minHeight: '320px' }}>
          <Text role="alert" fontSize="16px" color="secondary.1" style={{ opacity: 0.8, textAlign: 'center' }}>
            Ocurrió un error, intentá nuevamente
          </Text>
        </Box>
      )}
      {!loading && !estudiosFailed && (
        <>
          <FadeIn>
            <Text
              as="h2"
              fontSize={['18px', '18px', '18px', '20px', '22px']}
              fontWeight={500}
              color={'primary.0'}
              mb={'24px'}
              style={{ lineHeight: 1.4 }}
            >
              Publicaciones semanales acerca de la evolución de la economía
            </Text>
          </FadeIn>
          <ResponsiveGapBox width="100%" display={'flex'} flexDirection={['column', 'row', 'row', 'row']}>
            <ContainerHover
              as="a"
              href={getEstudios?.weekly?.[0]?.fileKey ? `/gateway/cdn/${getEstudios.weekly[0].fileKey}` : undefined}
              target="_blank"
              rel="noopener noreferrer"
              width={['100%', '100%', '50%', '50%']}
              aria-label={`Abrir Semana económica ${getEstudios?.weekly?.[0]?.title ?? ''}`}
            >
              {getEstudios?.weekly?.[0]?.period && <PeriodTag>{getEstudios.weekly[0].period}</PeriodTag>}
              <Box height={['220px', '220px', '160px', '160px', '180px']}>
                <CardImg
                  width={300}
                  height={180}
                  src="/CDN/Get/pictograma_semana_actual_l"
                  alt={getEstudios?.weekly?.[0]?.title ?? 'Semana económica'}
                  decoding="async"
                  loading="eager"
                  style={{ objectFit: 'contain' }}
                />
              </Box>
              <Box flexDirection="column" justifyContent="center" px="16px" mt="16px" style={{ gap: '2px' }}>
                <Text fontSize={['22px', '22px', '20px', '26px', '28px']} fontWeight={600} color="secondary.1" style={{ lineHeight: 1.35 }}>
                  {getEstudios?.weekly?.[0]?.title}
                </Text>
                <Text fontSize={['16px', '16px', '15px', '16px']} color="secondary.1" style={{ lineHeight: 1.5, opacity: 0.8 }}>
                  {getEstudios?.weekly?.[0]?.detail}
                </Text>
              </Box>
            </ContainerHover>

            <ContainerHover
              as="a"
              href="/web/pulso_PBA"
              target="_blank"
              rel="noopener noreferrer"
              width={['100%', '100%', '50%', '50%']}
              aria-label="Abrir Pulso PBA — El termómetro de actividad de la Provincia de Buenos Aires"
            >
              <Box height={['220px', '220px', '160px', '160px', '180px']}>
                <CardImg
                  width={300}
                  height={180}
                  src="/CDN/Get/pictograma_pulso_pba_l"
                  alt="Pulso PBA"
                  decoding="async"
                  loading="eager"
                  style={{ objectFit: 'contain' }}
                />
              </Box>
              <Box flexDirection="column" justifyContent="center" px="16px" mt="16px" style={{ gap: '2px' }}>
                <Text fontSize={['22px', '22px', '20px', '26px', '28px']} fontWeight={600} color="secondary.1" style={{ lineHeight: 1.35 }}>
                  Pulso PBA
                </Text>
                <Text fontSize={['16px', '16px', '15px', '16px']} color="secondary.1" style={{ lineHeight: 1.5, opacity: 0.8 }}>
                  El termómetro de actividad de la Provincia de Buenos Aires
                </Text>
              </Box>
            </ContainerHover>
          </ResponsiveGapBox>

          <ResponsiveGapBox
            width="100%"
            marginTop={'24px'}
            display={'flex'}
            flexDirection={['column', 'row', 'row', 'row']}
            marginBottom={'24px'}
          >
            <ContainerHover
              as="a"
              href={getEstudios?.ibp?.[0]?.fileKey ? `/gateway/cdn/${getEstudios.ibp[0].fileKey}` : undefined}
              target="_blank"
              rel="noopener noreferrer"
              width={['100%', '100%', '49.5%', '49.5%']}
              style={{ flexDirection: 'row', alignItems: 'stretch', gap: '20px' }}
              aria-label={`Abrir Índice Banco Provincia ${getEstudios?.ibp?.[0]?.title ?? ''}`}
            >
              {getEstudios?.ibp?.[0]?.period && <PeriodTag>{getEstudios.ibp[0].period}</PeriodTag>}
              <Box height={'150px'} width={['50%', '50%', '40%', '40%']}>
                <CardImg
                  height={150}
                  width={200}
                  src="/CDN/Get/pictograma_ibp"
                  alt={getEstudios?.ibp?.[0]?.title ?? 'IBP'}
                  decoding="async"
                  loading="lazy"
                />
              </Box>
              <Box flexDirection="column" justifyContent="center" width={['50%', '50%', '60%', '60%']} style={{ gap: '2px' }}>
                <Text fontSize={['18px', '18px', '14px', '18px', '20px']} fontWeight={600} color="secondary.1" style={{ lineHeight: 1.35 }}>
                  {getEstudios?.ibp?.[0]?.title}
                </Text>
                <Text fontSize={['14px', '14px', '12px', '14px', '15px']} color="secondary.1" style={{ lineHeight: 1.5, opacity: 0.8 }}>
                  {getEstudios?.ibp?.[0]?.detail}
                </Text>
              </Box>
            </ContainerHover>

            <ContainerHover
              as="a"
              href={getEstudios?.dt?.[0]?.fileKey ? `/gateway/cdn/${getEstudios.dt[0].fileKey}` : undefined}
              target="_blank"
              rel="noopener noreferrer"
              width={['100%', '100%', '49.5%', '49.5%']}
              style={{ flexDirection: 'row', alignItems: 'stretch', gap: '20px' }}
              aria-label={`Abrir Documentos de Trabajo ${getEstudios?.dt?.[0]?.title ?? ''}`}
            >
              {getEstudios?.dt?.[0]?.period && <PeriodTag>{getEstudios.dt[0].period}</PeriodTag>}
              <Box height={'150px'} width={['50%', '50%', '40%', '40%']}>
                <CardImg
                  height={150}
                  width={200}
                  src="/CDN/Get/pictograma_documentos_trabajo"
                  alt={getEstudios?.dt?.[0]?.title ?? 'Documentos de Trabajo'}
                  decoding="async"
                  loading="lazy"
                />
              </Box>
              <Box flexDirection="column" justifyContent="center" width={['50%', '50%', '60%', '60%']} style={{ gap: '2px' }}>
                <Text fontSize={['18px', '18px', '14px', '18px', '20px']} fontWeight={600} color="secondary.1" style={{ lineHeight: 1.35 }}>
                  {getEstudios?.dt?.[0]?.title}
                </Text>
                <Text fontSize={['14px', '14px', '12px', '14px', '15px']} color="secondary.1" style={{ lineHeight: 1.5, opacity: 0.8 }}>
                  {getEstudios?.dt?.[0]?.detail}
                </Text>
              </Box>
            </ContainerHover>
          </ResponsiveGapBox>
        </>
      )}
      <InformesAnteriores />
    </Container>
  );
};

const cardFadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const ResponsiveGapBox = styled(Box)`
  gap: 16px;
`;

const FadeIn = styled.div`
  animation: ${cardFadeIn} 0.4s ease-out both;
`;

const ContainerHover = styled(Box)`
  position: relative;
  background-color: white;
  margin-bottom: 0;
  /* reset defaults del <button>/<a> cuando se usa via as */
  border: none;
  text-align: left;
  font: inherit;
  color: inherit;
  text-decoration: none;
  /* mobile-first: chico + lg sutil */
  border-top-left-radius: ${({ theme }) => theme.radii.sm};
  border-top-right-radius: ${({ theme }) => theme.radii.lg};
  border-bottom-left-radius: ${({ theme }) => theme.radii.lg};
  border-bottom-right-radius: ${({ theme }) => theme.radii.sm};
  overflow: hidden;
  display: flex;
  flex-direction: column;
  transition: box-shadow 0.3s ease;
  padding: ${({ theme }) => theme.space[6]}px;
  animation: ${cardFadeIn} 0.4s ease-out both;

  :hover {
    cursor: pointer;
    box-shadow: 0px 2px 10px 2px rgba(0, 0, 0, 0.46);
  }

  /* tablet: md + pill (24) */
  @media (min-width: 768px) {
    border-top-left-radius: ${({ theme }) => theme.radii.md};
    border-top-right-radius: ${({ theme }) => theme.radii.pill};
    border-bottom-left-radius: ${({ theme }) => theme.radii.pill};
    border-bottom-right-radius: ${({ theme }) => theme.radii.md};
  }

  /* desktop wide: lg + 32px */
  @media (min-width: 1280px) {
    border-top-left-radius: ${({ theme }) => theme.radii.lg};
    border-top-right-radius: 32px;
    border-bottom-left-radius: 32px;
    border-bottom-right-radius: ${({ theme }) => theme.radii.lg};
  }
`;

const CardImg = styled.img`
  object-fit: cover;
  object-position: center;
  width: 100%;
  height: 100%;
  /* mobile: acompaña el radi asimétrico de ContainerHover */
  border-top-left-radius: ${({ theme }) => theme.radii.sm};
  border-top-right-radius: ${({ theme }) => theme.radii.lg};
  border-bottom-left-radius: ${({ theme }) => theme.radii.lg};
  border-bottom-right-radius: ${({ theme }) => theme.radii.sm};

  @media (min-width: 768px) {
    border-top-left-radius: ${({ theme }) => theme.radii.md};
    border-top-right-radius: ${({ theme }) => theme.radii.pill};
    border-bottom-left-radius: ${({ theme }) => theme.radii.pill};
    border-bottom-right-radius: ${({ theme }) => theme.radii.md};
  }

  @media (min-width: 1280px) {
    border-top-left-radius: ${({ theme }) => theme.radii.lg};
    border-top-right-radius: 32px;
    border-bottom-left-radius: 32px;
    border-bottom-right-radius: ${({ theme }) => theme.radii.lg};
  }
`;

const PeriodTag = styled.span`
  position: absolute;
  top: 24px;
  right: 0;
  background-color: ${({ theme }) => theme.colors.primary[0]};
  color: ${({ theme }) => theme.colors.white};
  font-size: 10px;
  font-weight: 500;
  padding: 4px 10px;
  border-top-left-radius: 3px;
  border-bottom-left-radius: 3px;
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
  z-index: 1;
  line-height: 1.2;

  @media (min-width: 768px) and (max-width: 1279px) {
    top: 14px;
  }

  @media (min-width: 1280px) {
    font-size: 12px;
    padding: 6px 14px;
    border-top-left-radius: ${({ theme }) => theme.radii.sm};
    border-bottom-left-radius: ${({ theme }) => theme.radii.sm};
  }
`;
